import { plural } from './format';

/*
  Единые правила отображения для экранов Jack OS:
  - деньги: «12 500 ฿» (неразрывные пробелы — сумма не переносится);
  - количество: «1 ожидание», «24 события» — только через countLabel;
  - телефоны в операциях скрываются маской.
*/

const NBSP = '\u00a0';

/** «12 500 ฿» or «336 763,90 ฿» when satang are present. Never rounds a cash remainder away. */
export function formatBaht(value: number, currency = 'THB'): string {
  const code = (currency || 'THB').toUpperCase();
  const satang = Math.round(value * 100);
  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: satang % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(satang / 100).replace(/[\s\u202f]/g, NBSP);
  const sign: Record<string, string> = { THB: '฿', USD: '$', EUR: '€' };
  return `${formatted}${NBSP}${sign[code] ?? code}`;
}

/**
 * Только THB: «Сегодня» и экраны сущностей показывают суммы исключительно в бат.
 * Сумма в другой валюте не получает чужой знак ($, ₽) — вместо неё пометка.
 */
export function formatThbOnly(value: number, currency?: string | null, other = 'не в THB'): string {
  const code = (currency || 'THB').trim().toUpperCase();
  return code === 'THB' ? formatBaht(value) : other;
}

/** Подпись валюты рядом с числом: код ISO («USD», «EUR»); рубли — «руб.» (правило Margin: нигде нет «₽», «$» и «RUB»). */
export function currencyCodeLabel(currency?: string | null): string | null {
  const code = (currency ?? '').trim().toUpperCase();
  if (!code || code === 'UNKNOWN') return null;
  if (code === 'RUB') return 'руб.';
  return code;
}

/**
 * Сумма в своей валюте без пересчёта: THB — «12 500 ฿»; другая валюта — число с кодом
 * («40 USD», «120 EUR», «1 000 руб.»), никогда со знаком ฿ и никогда без числа.
 * Без валюты — число и «валюта не указана».
 */
export function formatMoneyCode(value: number, currency?: string | null): string {
  const code = (currency ?? '').trim().toUpperCase();
  if (code === 'THB') return formatBaht(value);
  const number = formatBaht(value).replace(/\u00a0฿$/, '');
  const label = currencyCodeLabel(code);
  return label ? `${number}${NBSP}${label}` : `${number}${NBSP}· валюта не указана`;
}

/** Гость в карточке: код `owner` показываем по-русски, пустое — «не указано». */
export function guestLabel(name: string | null | undefined, empty = 'Гость не указан'): string {
  const raw = name?.trim() ?? '';
  if (!raw) return empty;
  if (/^owner$/i.test(raw)) return 'Владелец';
  if (/^from owner$/i.test(raw)) return 'от владельца';
  return raw;
}

/** «1 ожидание», «3 ожидания», «24 ожидания» — число и правильная форма. */
export function countLabel(n: number, one: string, few: string, many: string): string {
  return `${n.toLocaleString('ru-RU')} ${plural(n, one, few, many)}`;
}

const PHONE = /(?<![\w+\-./:])(?:\+\d|0)(?:[ ()-]?\d){7,13}(?![\w])/g;

/** «+371 29 214 146» → «+•••146». Даты, суммы до 9 цифр и идентификаторы не трогаются. */
export function maskPhones(text: string): string {
  return text.replace(PHONE, (match) => {
    const digits = match.replace(/\D/g, '');
    return `${match.trim().startsWith('+') ? '+' : ''}•••${digits.slice(-3)}`;
  });
}

export function maskPhonesDeep<T>(value: T): T {
  if (typeof value === 'string') return maskPhones(value) as T;
  if (Array.isArray(value)) return value.map((item) => maskPhonesDeep(item)) as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => [key, maskPhonesDeep(item)])) as T;
  }
  return value;
}

/**
 * Итог брони. Если общая сумма в источнике пуста, но месячная цена задана,
 * считаем «целые месяцы × цена + остаток дней × цена / 30» и помечаем как расчёт.
 */
export function bookingTotal(
  total: number | null,
  monthly: number | null,
  checkIn: string | null,
  checkOut: string | null,
): { value: number | null; estimated: boolean; needsPriceCheck: boolean } {
  const needsPriceCheck = total === 0 || monthly === 0;
  if (total != null) return { value: total, estimated: false, needsPriceCheck };
  if (monthly == null || !checkIn || !checkOut) return { value: null, estimated: false, needsPriceCheck };
  const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(checkIn);
  const b = /^(\d{4})-(\d{2})-(\d{2})/.exec(checkOut);
  if (!a || !b) return { value: null, estimated: false, needsPriceCheck };
  const start = Date.UTC(+a[1]!, +a[2]! - 1, +a[3]!);
  const end = Date.UTC(+b[1]!, +b[2]! - 1, +b[3]!);
  if (!(end > start)) return { value: null, estimated: false, needsPriceCheck };
  let months = (+b[1]! - +a[1]!) * 12 + (+b[2]! - +a[2]!);
  let anchor = Date.UTC(+a[1]!, +a[2]! - 1 + months, +a[3]!);
  if (anchor > end) { months -= 1; anchor = Date.UTC(+a[1]!, +a[2]! - 1 + months, +a[3]!); }
  const days = Math.round((end - anchor) / 86_400_000);
  return { value: Math.round(months * monthly + (days * monthly) / 30), estimated: true, needsPriceCheck };
}
